import React, { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import getConfig from 'next/config';
import Modal from 'components/Modal';
import { useToggle } from 'hooks/useToogle';
import { useIsMobileWithBreakpoint } from 'hooks/useIsMobileWithBreakpoint';
import { handleEnterKey } from 'utils/helpers/accessible-events';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import PlayButton from '../AudioPlayer/components/PlayButton';
import BackForwardControls from '../AudioPlayer/components/BackForwardControls';
import ProgressBar from '../AudioPlayer/components/ProgressBar';
import VolumeControlBar from '../AudioPlayer/components/VolumeControl';
import TimeValues from './components/TimeValues';
import ExtraControls from './components/ExtraControls';

import styles from './styles.module.scss';

const {
	publicRuntimeConfig: { baseUrl }
} = getConfig();

const getWidgetFrame = (title, widgetId) =>
	`<iframe title="${title}" width="500" height="140" src="${`${baseUrl}/embedded/widgets/${widgetId}`}" />`;

const PodcastPlayer = forwardRef(({ track, autoPlay }, ref) => {
	const { src, title, widgetId } = track;
	const [isModalOpened, openModal, closeModal] = useToggle(false);
	const [closeModalTimeout, setCloseModalTimeout] = useState(null);
	const audioPlayerRef = useRef(null);
	const progressBarRef = useRef(null);
	const volumeBarRef = useRef(null);
	const isMobile = useIsMobileWithBreakpoint('mobileLarge');

	const sharedWidget = widgetId ? getWidgetFrame(title, widgetId) : null;

	const copyWidgetCode = () => {
		const closeModalByTimeout = setTimeout(closeModal, [2000]);
		navigator.clipboard
			.writeText(sharedWidget)
			.then(openModal)
			.then(setCloseModalTimeout(closeModalByTimeout));
	};

	const {
		isPlaying,
		currentTime,
		duration,
		calculateTime,
		playPauseToggle,
		changeRange,
		dropRange,
		changeVolume,
		rewind
	} = useAudioPlayer({
		audioPlayerRef,
		progressBarRef,
		volumeBarRef,
		src
	});

	useEffect(() => {
		if (autoPlay) {
			dropRange();
		}
	}, [src]);

	const handleCloseModal = () => {
		if (closeModalTimeout) {
			clearTimeout(closeModalTimeout);
		}
		closeModal();
	};

	const [visible, setVisible] = useState(false);
	const forwardButtonRef = useRef(null);

	const handleKeyDown = (event) => {
		handleEnterKey(event, () => {
			event.preventDefault();
			setVisible(true);
			setTimeout(() => {
				forwardButtonRef.current.focus();
			}, 0);
		});
	};

	const handleFocus = (event) => {
		if (event.target === event.currentTarget) {
			setVisible(false);
		}
	};

	const handleBlur = (event) => {
		const { relatedTarget, currentTarget } = event;

		if (!currentTarget.contains(relatedTarget)) {
			setVisible(false);
		}
	};

	return (
		<div
			tabIndex={0}
			aria-label={title}
			onKeyDown={handleKeyDown}
			onFocus={handleFocus}
			onBlur={handleBlur}
			onMouseEnter={() => setVisible(true)}
			onMouseLeave={() => setVisible(false)}
			role="toolbar"
			ref={ref}
		>
			<div className={styles.PodcastPlayer} inert={visible ? false : ''}>
				{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
				<audio
					ref={audioPlayerRef}
					src={src || ''}
					autoPlay={autoPlay}
					preload="metadata"
				/>
				<BackForwardControls
					rewindForward={rewind(15)}
					rewindBack={rewind(-15)}
					forwardButtonRef={forwardButtonRef}
				>
					<PlayButton
						isPlaying={isPlaying}
						play={playPauseToggle}
						rewindForward={rewind(15)}
						rewindBack={rewind(-15)}
						theme="bordered"
					/>
				</BackForwardControls>
				{!isMobile && (
					<VolumeControlBar
						volumeBarRef={volumeBarRef}
						changeVolume={changeVolume}
						theme="podcast"
					/>
				)}
				<div className={styles.PodcastPlayer__Content}>
					<span className={styles.PodcastTitle}>{title}</span>
					<div className={styles.PodcastBody}>
						{isMobile && (
							<VolumeControlBar
								volumeBarRef={volumeBarRef}
								changeVolume={changeVolume}
								theme="podcast"
								isMobile={isMobile}
							/>
						)}
						<TimeValues
							key={duration}
							currentTime={currentTime}
							duration={duration}
							calculateFunction={calculateTime}
						>
							<ProgressBar
								reference={progressBarRef}
								onChangeCallback={changeRange}
								rewindForward={rewind(15)}
								rewindBack={rewind(-15)}
							/>
						</TimeValues>
					</div>
				</div>
				<ExtraControls
					{...{
						trackSrc: src,
						copyWidgetCode: widgetId ? copyWidgetCode : null
					}}
				/>
				{isModalOpened && (
					<Modal
						header=""
						isShowModal={isModalOpened}
						onRequestClose={handleCloseModal}
						theme="message"
					>
						<div className={styles.ModalContent}>
							Код скопирован в буфер обмена
						</div>
					</Modal>
				)}
			</div>
		</div>
	);
});

PodcastPlayer.initialProps = {
	autoPlay: false
};

PodcastPlayer.propTypes = {
	track: PropTypes.shape({
		src: PropTypes.string,
		title: PropTypes.string,
		key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		widgetId: PropTypes.number
	}),
	autoPlay: PropTypes.bool
};

export default PodcastPlayer;
